import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from '../../axiosConfig';
import { useNavigate, useSearchParams } from 'react-router-dom';
import '../../styles/RegisterInvitedTrainer.css';

const RegisterInvitedTrainer = () => {
    const { t } = useTranslation('auth');
    const [formData, setFormData] = useState({
        password: '',
        firstName: '',
        lastName: '',
        phone: '',
        country: '',
        city: '',
        postalCode: '',
        street: '',
        buildingNumber: '',
        apartmentNumber: '',
        statuteAccepted: false
    });

    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [fieldErrors, setFieldErrors] = useState({});
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');

    const handleChange = (e) => {
        const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
        setFormData({ ...formData, [e.target.name]: value });
        if (fieldErrors[e.target.name]) {
            setFieldErrors({ ...fieldErrors, [e.target.name]: '' });
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');
        setFieldErrors({});

        if (!token) {
            setError(t('register.invalidToken'));
            return;
        }

        const requestBody = {
            password: formData.password,
            firstName: formData.firstName,
            lastName: formData.lastName,
            phone: formData.phone,
            statuteAccepted: formData.statuteAccepted,
            address: {
                country: formData.country,
                city: formData.city,
                postalCode: formData.postalCode,
                street: formData.street,
                buildingNumber: formData.buildingNumber,
                apartmentNumber: formData.apartmentNumber
            }
        };

        try {
            await axios.post(`/api/v1/auth/registerToExistingSchool/trainer?token=${token}`, requestBody);
            setSuccess(t('register.invitedTrainerSuccess'));
            setTimeout(() => navigate('/'), 2000);
        } catch (err) {
            console.error('Registration error:', err);
            if (err.response?.data?.errors) {
                const errors = err.response.data.errors;
                const mappedErrors = {};

                Object.keys(errors).forEach(key => {
                    if (key.startsWith('address.')) {
                        const fieldName = key.replace('address.', '');
                        mappedErrors[fieldName] = errors[key];
                    } else {
                        mappedErrors[key] = errors[key];
                    }
                });

                setFieldErrors(mappedErrors);
                setError(err.response.data.message || t('register.validation.validationError'));
            } else {
                setError(t('register.invitedRegistrationFailed'));
            }
        }
    };

    const handleBack = () => {
        navigate(-1);
    };

    if (!token) {
        return (
            <div className="register-invited-trainer-container">
                <div className="register-invited-trainer-left-panel">
                    <div className="register-invited-trainer-left-content">
                        <h2>
                            {t('register.invitedWelcome.joinThe')}
                            <span className="highlight">{t('register.invitedWelcome.ourTeam')}</span>
                        </h2>
                    </div>
                </div>
                <div className="register-invited-trainer-right-panel">
                    <div className="register-invited-trainer-form-container">
                        <h1>Error</h1>
                        <p className="register-invited-trainer-error">{t('register.invalidToken')}</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="register-invited-trainer-container">
            <div className="register-invited-trainer-left-panel">
                <div className="register-invited-trainer-left-content">
                    <h2>
                        {t('register.invitedWelcome.joinThe')}
                        <span className="highlight">{t('register.invitedWelcome.ourTeam')}</span>
                    </h2>
                </div>
            </div>
            <div className="register-invited-trainer-right-panel">
                <button className="register-invited-trainer-back-button" onClick={handleBack}>
                    {t('register.back')}
                </button>
                <div className="register-invited-trainer-form-container">
                    <h1>{t('register.invitedTrainerTitle')}</h1>
                    <form onSubmit={handleSubmit} className="register-invited-trainer-form">
                        <div className="register-invited-trainer-form-group full-width">
                            <input
                                type="password"
                                name="password"
                                placeholder={t('register.password')}
                                value={formData.password}
                                required
                                onChange={handleChange}
                                className={fieldErrors.password ? 'input-error' : ''}
                            />
                            {fieldErrors.password && <span className="field-error">{fieldErrors.password}</span>}
                        </div>

                        <div className="register-invited-trainer-form-row">
                            <div className="register-invited-trainer-form-group">
                                <input
                                    type="text"
                                    name="firstName"
                                    placeholder={t('register.firstName')}
                                    value={formData.firstName}
                                    required
                                    onChange={handleChange}
                                    className={fieldErrors.firstName ? 'input-error' : ''}
                                />
                                {fieldErrors.firstName && <span className="field-error">{fieldErrors.firstName}</span>}
                            </div>
                            <div className="register-invited-trainer-form-group">
                                <input
                                    type="text"
                                    name="lastName"
                                    placeholder={t('register.lastName')}
                                    value={formData.lastName}
                                    required
                                    onChange={handleChange}
                                    className={fieldErrors.lastName ? 'input-error' : ''}
                                />
                                {fieldErrors.lastName && <span className="field-error">{fieldErrors.lastName}</span>}
                            </div>
                        </div>

                        <div className="register-invited-trainer-form-group full-width">
                            <input
                                type="text"
                                name="phone"
                                placeholder={t('register.phone')}
                                value={formData.phone}
                                required
                                onChange={handleChange}
                                className={fieldErrors.phone ? 'input-error' : ''}
                            />
                            {fieldErrors.phone && <span className="field-error">{fieldErrors.phone}</span>}
                        </div>

                        <h2>{t('register.addressSection')}</h2>

                        <div className="register-invited-trainer-form-row">
                            <div className="register-invited-trainer-form-group">
                                <input
                                    type="text"
                                    name="country"
                                    placeholder={t('register.country')}
                                    value={formData.country}
                                    required
                                    onChange={handleChange}
                                    className={fieldErrors.country ? 'input-error' : ''}
                                />
                                {fieldErrors.country && <span className="field-error">{fieldErrors.country}</span>}
                            </div>
                            <div className="register-invited-trainer-form-group">
                                <input
                                    type="text"
                                    name="city"
                                    placeholder={t('register.city')}
                                    value={formData.city}
                                    required
                                    onChange={handleChange}
                                    className={fieldErrors.city ? 'input-error' : ''}
                                />
                                {fieldErrors.city && <span className="field-error">{fieldErrors.city}</span>}
                            </div>
                        </div>

                        <div className="register-invited-trainer-form-row">
                            <div className="register-invited-trainer-form-group">
                                <input
                                    type="text"
                                    name="postalCode"
                                    placeholder={t('register.postalCode')}
                                    value={formData.postalCode}
                                    required
                                    onChange={handleChange}
                                    className={fieldErrors.postalCode ? 'input-error' : ''}
                                />
                                {fieldErrors.postalCode && <span className="field-error">{fieldErrors.postalCode}</span>}
                            </div>
                            <div className="register-invited-trainer-form-group">
                                <input
                                    type="text"
                                    name="street"
                                    placeholder={t('register.street')}
                                    value={formData.street}
                                    required
                                    onChange={handleChange}
                                    className={fieldErrors.street ? 'input-error' : ''}
                                />
                                {fieldErrors.street && <span className="field-error">{fieldErrors.street}</span>}
                            </div>
                        </div>

                        <div className="register-invited-trainer-form-row">
                            <div className="register-invited-trainer-form-group">
                                <input
                                    type="text"
                                    name="buildingNumber"
                                    placeholder={t('register.buildingNumber')}
                                    value={formData.buildingNumber}
                                    required
                                    onChange={handleChange}
                                    className={fieldErrors.buildingNumber ? 'input-error' : ''}
                                />
                                {fieldErrors.buildingNumber && <span className="field-error">{fieldErrors.buildingNumber}</span>}
                            </div>
                            <div className="register-invited-trainer-form-group">
                                <input
                                    type="text"
                                    name="apartmentNumber"
                                    placeholder={t('register.apartmentNumber')}
                                    value={formData.apartmentNumber}
                                    onChange={handleChange}
                                />
                            </div>
                        </div>

                        <div className="register-invited-trainer-checkbox-group">
                            <label className="register-invited-trainer-checkbox-label">
                                <input
                                    type="checkbox"
                                    name="statuteAccepted"
                                    checked={formData.statuteAccepted}
                                    onChange={handleChange}
                                    required
                                    className="register-invited-trainer-checkbox"
                                />
                                <span>{t('register.acceptTermsWithLink')}</span>
                            </label>
                            {fieldErrors.statuteAccepted && <span className="field-error">{fieldErrors.statuteAccepted}</span>}
                        </div>

                        {error && <p className="register-invited-trainer-error">{error}</p>}
                        {success && <p className="register-invited-trainer-success">{success}</p>}

                        <button type="submit" className="register-invited-trainer-submit-btn">
                            {t('register.registerButton')}
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default RegisterInvitedTrainer;
